import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Column, Row, Typography } from '@tempmee/react';
import { OutlinePlusIcon } from '@tempmee/react-icons';
import { colors, shadows } from '@tempmee/tailwind';
import { useGetPaymentMethods, useSelectPaymentMethod } from '~/domain/Payments';
import { DeletePaymentModal } from './DeletePaymentModal';
import { PaymentLine } from './PaymentLine';

const CARD_STYLE = {
  borderRadius: 8,
  backgroundColor: colors.shades[0],
  boxShadow: shadows['elevation-4'],
  paddingBottom: 28,
};

const LIST_INDENT_STYLE = { marginLeft: 36 };

const BUTTON_RESET_STYLE = { all: 'unset', cursor: 'pointer' };

export const PaymentCard = ({
  selectedPayment,
  setSelectedPayment,
  title,
  description,
  data,
  iconSrc,
  // IconComponent?: React.ComponentType — falls back to <img src={iconSrc}>
  IconComponent = null,
  addPaymentText,
  handleAddPaymentButton,
  addDisabled = false,
  showExpiration = false,
  dataCy,
  renderLineAction,
}) => {
  const user = useSelector((state) => state.user.selectedChildOffice ?? state.user.user);
  const { paymentMethods: allPayments } = useGetPaymentMethods();
  const selectPayment = useSelectPaymentMethod();
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const showActions = user?.is_pre_approved || allPayments?.length > 1;

  const handleRadioButtonClick = (item) => {
    setSelectedPayment(item);
  };

  const handleOnDelete = (item) => {
    setPaymentToDelete(item);
  };

  const handleMakeDefault = (paymentId) => {
    selectPayment.mutate(paymentId);
  };

  return (
    <Box
      data-cy={dataCy}
      style={CARD_STYLE}
      pt={8}
      pl={10}
      pr={8}
    >
      <Row align="center" gap={2} pb={2}>
        {IconComponent ? (
          <IconComponent size={24} color={colors.secondary[500]} />
        ) : (
          <img src={iconSrc} alt="payment-icon" />
        )}
        <Typography variant="body1" bold color={colors.neutral[600]}>
          {title}
        </Typography>
      </Row>

      <Typography italic color={colors.neutral[600]}>
        {description}
      </Typography>

      <Column gap={3} pt={8} style={LIST_INDENT_STYLE}>
        {data?.map((item) => (
          <PaymentLine
            key={item.id}
            item={item}
            handleMakeDefault={handleMakeDefault}
            handleOnDelete={handleOnDelete}
            handleRadioButtonClick={handleRadioButtonClick}
            isSelected={selectedPayment?.id === item.id}
            showActions={showActions}
            showExpiration={showExpiration}
            renderAction={renderLineAction}
          />
        ))}
      </Column>

      <Row justify="end" pt={10}>
        <button
          data-cy="btn-add-payment-method"
          type="button"
          style={{
            ...BUTTON_RESET_STYLE,
            cursor: addDisabled ? 'default' : BUTTON_RESET_STYLE.cursor,
          }}
          disabled={addDisabled}
          onClick={handleAddPaymentButton}
        >
          <Row align="center" gap={2}>
            <OutlinePlusIcon color={addDisabled ? colors.neutral[400] : colors.secondary[500]} />
            <Typography
              variant="body1"
              bold
              color={addDisabled ? colors.neutral[400] : colors.secondary[500]}
            >
              {addPaymentText}
            </Typography>
          </Row>
        </button>
      </Row>

      {paymentToDelete && (
        <DeletePaymentModal
          paymentToDelete={paymentToDelete}
          onClose={() => setPaymentToDelete(null)}
        />
      )}
    </Box>
  );
};
